import { NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { EventAttendeeQuestion } from '@models/event-attendance/event-attendee-question.model';
import { CreateEventTicketOrderSessionTicket } from '@models/events/dto/create-event-ticket-order-session.request';
import { EventAttendeeTicketOrder } from '@models/events/event-attendee-ticket-order.model';
import { EventSlot } from '@models/events/event-slot.model';
import { EventTicket } from '@models/events/event-ticket.model';
import { Event } from '@models/events/event.model';
import { LegalInfoLinkType } from '@models/legal-info/enums/legal-info-link-type.enum';
import { User } from '@models/users/user.model';
import { BookEventSlotChooseSlotComponent } from '@modules/events/components/book-event-slot-choose-slot/book-event-slot-choose-slot.component';
import { GetBoughtTicketsDialog } from '@modules/events/dialogs/get-bought-tickets/get-bought-tickets.dialog';
import { ProgressIndicatorComponent } from '@modules/shared/components/progress-indicator/progress-indicator.component';
import { AddToCalendarDialog } from '@modules/shared/dialogs/add-to-calendar/add-to-calendar.dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EventService } from '@services/events/event.service';
import { NotificationService } from '@services/shared/notification.service';
import { ScreenWidthService } from '@services/shared/screen-width.service';
import { TicketService } from '@services/tickets/ticket.service';
import { DateUtils } from '@utils/date-utils';

export enum TimeslotStep {
  SWITCH_ORDER = 1,
  SELECT_SLOT = 2,
  CONFIRMATION = 3,
  SUCCESS = 4,
}
@Component({
  selector: 'app-change-slot',
  standalone: true,
  imports: [
    MatDialogContent,
    ProgressIndicatorComponent,
    TranslateModule,
    BookEventSlotChooseSlotComponent,
    NgTemplateOutlet,
    MatCheckbox,
  ],
  templateUrl: './change-slot.dialog.html',
  styleUrl: './change-slot.dialog.scss',
  providers: [
    EventService,
    NotificationService,
    TranslateService,
    ScreenWidthService,
    TicketService,
  ],
})
export class ChangeSlotDialog implements OnInit {
  form?: FormGroup;
  event?: Event;
  order?: EventAttendeeTicketOrder;
  loggedUser?: User;
  step: number = 1;
  steps: TimeslotStep[] = [
    TimeslotStep.SWITCH_ORDER,
    TimeslotStep.SELECT_SLOT,
    TimeslotStep.CONFIRMATION,
    TimeslotStep.SUCCESS,
  ];
  isDesktop: boolean = true;
  currentStep = this.steps[0];
  numberOfSteps = this.steps.length - 1;
  chosenTicket: EventTicket;
  chosenSlot?: EventSlot;

  chosenSlotDataFields?: EventAttendeeQuestion[];

  orderTickets: CreateEventTicketOrderSessionTicket[] = [];
  fullPrice: number = 0;
  acceptTermsAndConditions: boolean = false;
  acceptDataPrivacy: boolean = false;
  acceptMarketing: boolean = false;
  transferInProgress: boolean = false;

  get currentStepNumber(): number {
    return this.steps.indexOf(this.currentStep) + 1;
  }

  addToCalendar() {
    this.dialog.open(AddToCalendarDialog, {
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        eventSlot: this.order?.eventSlot,
      },
      panelClass: ['normal-dialog', 'event-dialog', 'overlay-theme'],
    });
  }

  addSlotTicket(slot: EventSlot) {
    if (!slot.id || !this.chosenTicket?.id) {
      return;
    }
    const existingTicketIdx = this.orderTickets.findIndex(
      (orderTicket) => orderTicket.eventSlotId === slot.id,
    );

    if (existingTicketIdx !== -1) {
      this.orderTickets[existingTicketIdx].quantity += 1;
    } else {
      this.orderTickets.push({
        eventSlotId: slot.id,
        eventTicketId: this.chosenTicket.id,
        quantity: 1,
        price: this.chosenTicket.price,
      });
    }

    this.calculateFullPrice();
  }

  toggleTermsAndConditions() {
    this.acceptTermsAndConditions = !this.acceptTermsAndConditions;
  }
  toggleDataPrivacy() {
    this.acceptDataPrivacy = !this.acceptDataPrivacy;
  }

  toggleAcceptMarketing() {
    this.acceptMarketing = !this.acceptMarketing;
  }
  get termsAndConditionsLink(): string {
    const eventLegalTermsLink =
      this.event?.eventTemplate?.legalInfo?.getLinkByType(
        LegalInfoLinkType.TERMS_AND_CONDITIONS,
      );
    if (eventLegalTermsLink && eventLegalTermsLink.link) {
      return eventLegalTermsLink.link;
    }

    return 'https://eventpage.ai/terms-of-use/';
  }

  get dataPrivacyLink(): string {
    const eventLegalPrivacyLink =
      this.event?.eventTemplate?.legalInfo?.getLinkByType(
        LegalInfoLinkType.DATA_PRIVACY,
      );
    if (eventLegalPrivacyLink && eventLegalPrivacyLink.link) {
      return eventLegalPrivacyLink.link;
    }

    return 'https://eventpage.ai/privacy/';
  }
  checkIfCanTransferTickets() {
    return this.acceptTermsAndConditions && this.acceptDataPrivacy;
  }
  removeSlotTicket(slot: EventSlot) {
    if (!slot.id || !this.chosenTicket?.id) {
      return;
    }

    const existingTicketIdx = this.orderTickets.findIndex(
      (orderTicket) => orderTicket.eventSlotId === slot.id,
    );

    if (existingTicketIdx !== -1) {
      if (this.orderTickets[existingTicketIdx].quantity > 1) {
        this.orderTickets[existingTicketIdx].quantity -= 1;
      } else {
        this.orderTickets.splice(existingTicketIdx, 1);
      }

      this.calculateFullPrice();
    }
  }
  openGetBoughtTicketsDialog() {
    const ticket = this.order?.eventAttendeeTickets.find(
      (x) => x.eventAttendeeId === this.order?.eventAttendeeId,
    );
    const dialogRef = this.dialog.open(GetBoughtTicketsDialog, {
      maxWidth: '456px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        ticket: ticket,
        loggedUser: this.loggedUser,
        order: this.order,
      },
      panelClass: ['normal-dialog', 'new-normal-dialog'],
      backdropClass: '',
    });
  }

  changeEventSlot() {
    if (!this.event || !this.order || !this.chosenSlot) {
      return;
    }
    this.transferInProgress = true;
    this.eventService
      .changeOrderTimeSlot(this.event.id, this.order.uuid, this.chosenSlot.id)
      .subscribe({
        next: (res) => {
          this.transferInProgress = false;
          this.order = new EventAttendeeTicketOrder(res);
          this.dialogRef.close({ successOrder: this.order });
          // this.goNext();
        },
        error: (err) => {
          this.transferInProgress = false;
          if (err.message) {
            this.notificationService.error(err.message);
          } else if (err.error) {
            this.notificationService.error(err.error);
          }
        },
      });
  }

  downloadTicketPdf() {
    if (!this.order) {
      return;
    }

    let fileName = `${this.order.getDateDisplayFileName()}_${
      this.order.orderNumber
    }.pdf`;
    if (this.event) {
      fileName = `${this.order.getDateDisplayFileName()}_${this.event.getSanitizedName()}_${
        this.order.orderNumber
      }.pdf`;
    }

    this.eventService
      .downloadTicketOrderPdf(this.order.uuid, fileName)
      .subscribe({
        error: () => {
          this.notificationService.error(
            this.translateService.instant(
              'APP.EVENT_PAYMENT.COULD_NOT_DOWNLOAD',
            ),
          );
        },
      });
  }
  calculateFullPrice() {
    // let remainingLimit = this.voucherApplied?.limit ?? 0;

    this.fullPrice = this.orderTickets
      .map((orderTicket) => {
        let applicableQuantity = orderTicket.quantity;

        let price = orderTicket.price ?? 0;

        return (price && price > 0 ? price : 0) * applicableQuantity;
      })
      .reduce((a, b) => a + b, 0);

    if (this.order) {
      if (!this.order.hasPaymentIntent() && this.fullPrice >= 50) {
        // this.createTicketOrderSession();
      } else {
        this.order.totalPrice = this.fullPrice;

        this.order = new EventAttendeeTicketOrder(this.order);
      }
    }
  }

  get headerTitle(): string {
    switch (this.step) {
      case 1:
        return 'APP.REQUEST_REFUND.CHANGE_TIMESLOT';
      case 2:
        return 'APP.REQUEST_REFUND.CHANGE_TIMESLOT';
      case 3:
        return 'APP.REQUEST_REFUND.CONFIRM_SELECTION';
      case 4:
        return 'APP.REQUEST_REFUND.TRANSFER_SUCCESSFUL';
      default:
        return '';
    }
  }

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ChangeSlotDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private eventService: EventService,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private screenWidthService: ScreenWidthService,
    private ticketService: TicketService,
    private dialog: MatDialog,
  ) {
    this.screenWidthService
      .isDesktop()
      .pipe(takeUntilDestroyed())
      .subscribe((isDesktop) => (this.isDesktop = isDesktop));

    if (data) {
      this.event = data.event;
      this.order = data.order;
      this.loggedUser = data.loggedUser;
      if (this.order && this.order.eventSlot) {
        this.onSlotChosen(this.order.eventSlot);
        /*        this.order.eventAttendeeTickets.forEach((value, index) => {
          this.orderTickets.push({
            eventSlotId: this.order?.eventSlotId,
            eventTicketId: value.eventTicketId,
            quantity: 1,
            price: value.price,
          });
        });*/
      }
    }
    this.chosenTicket = data.ticket;
    this.form = this.fb.group({
      refundReason: ['', Validators.required],
      message: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    if (!this.event) return;

    this.ticketService.getTickets(this.event.id).subscribe({
      next: (res) => {
        const orderTicketName = res.find(
          (x) => x.name === this.chosenTicket.name,
        );
        if (orderTicketName) {
          this.chosenTicket = orderTicketName;
        }
      },
    });
  }

  onSlotChosen(slot: EventSlot) {
    this.chosenSlot = slot;
    this.acceptTermsAndConditions = false;
    this.acceptMarketing = false;
    this.acceptDataPrivacy = false;
    this.chosenSlotDataFields =
      this.chosenSlot?.getDataFieldsAsEventAttendeeQuestion();
  }

  previousStep() {
    const currentIndex = this.steps.indexOf(this.currentStep);
    if (this.step > 1) {
      this.step--;
      this.currentStep = this.steps[currentIndex - 1];
    } else {
      this.close();
    }
  }

  goNext() {
    this.step++;
  }

  close(close: boolean = false) {
    this.dialogRef.close({ close: close });
  }

  get chooseSlotValid(): boolean {
    if (!this.order) {
      return false;
    }

    return (
      !!this.chosenSlot &&
      this.chosenTicket.hasAvailableSlotsToChange(
        this.order.eventAttendeeTickets?.length || 1,
      )
    );
  }

  protected readonly TimeslotStep = TimeslotStep;
  protected readonly DateUtils = DateUtils;
}
